import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import type { BannerProps, BannerVariant } from './Alert.types';

/**
 * Variant-specific styling configuration for banners
 * Similar to Alert but optimized for full-width display
 */
const variantStyles: Record<
  BannerVariant,
  {
    container: string;
    icon: string;
    content: string;
    action: string;
    dismiss: string;
  }
> = {
  success: {
    container: 'bg-success-wash border-success-500/30',
    icon: 'text-success-500',
    content: 'text-success-500',
    action: 'text-success-500 hover:text-success-400 hover:bg-success-500/10',
    dismiss: 'text-success-500 hover:bg-success-500/10',
  },
  warning: {
    container: 'bg-warning-wash border-warning-500/30',
    icon: 'text-warning-500',
    content: 'text-warning-500',
    action: 'text-warning-500 hover:text-warning-400 hover:bg-warning-500/10',
    dismiss: 'text-warning-500 hover:bg-warning-500/10',
  },
  error: {
    container: 'bg-error-wash border-error-500/30',
    icon: 'text-error-500',
    content: 'text-error-500',
    action: 'text-error-500 hover:text-error-400 hover:bg-error-500/10',
    dismiss: 'text-error-500 hover:bg-error-500/10',
  },
  info: {
    container: 'bg-info-wash border-info-500/30',
    icon: 'text-info-500',
    content: 'text-info-500',
    action: 'text-info-500 hover:text-info-400 hover:bg-info-500/10',
    dismiss: 'text-info-500 hover:bg-info-500/10',
  },
  neutral: {
    container: 'bg-contrast-700 border-stroke-default',
    icon: 'text-neutral-400',
    content: 'text-neutral-300',
    action: 'text-neutral-300 hover:text-neutral-200 hover:bg-lift-3',
    dismiss: 'text-neutral-400 hover:bg-lift-3',
  },
};

/**
 * Default icons for each variant
 */
const defaultIcons: Record<BannerVariant, React.ReactElement> = {
  success: <CheckCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  error: <XCircleIcon className="h-5 w-5" />,
  info: <InformationCircleIcon className="h-5 w-5" />,
  neutral: <InformationCircleIcon className="h-5 w-5" />,
};

/**
 * Banner component for full-width page banners
 * Designed to span the entire container width with centered content
 *
 * @example
 * ```tsx
 * // Simple info banner
 * <Banner variant="info">
 *   New features are now available!
 * </Banner>
 *
 * // Warning banner with action
 * <Banner
 *   variant="warning"
 *   dismissible
 *   onDismiss={() => setShow(false)}
 *   action={{ label: 'Learn more', onClick: () => navigate('/info') }}
 * >
 *   Your subscription is expiring soon.
 * </Banner>
 * ```
 */
export function Banner({
  variant = 'info',
  children,
  icon,
  hideIcon = false,
  dismissible = false,
  onDismiss,
  action,
  className = '',
  ...props
}: BannerProps) {
  const styles = variantStyles[variant];
  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div
      role="alert"
      className={`
        w-full border-b
        ${styles.container}
        ${className}
      `.trim()}
      {...props}
    >
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Content area */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Icon */}
            {!hideIcon && (
              <div
                className={`flex-shrink-0 ${styles.icon}`}
                aria-hidden="true"
              >
                {displayIcon}
              </div>
            )}

            {/* Message */}
            <p className={`text-body-sm font-medium ${styles.content}`}>
              {children}
            </p>
          </div>

          {/* Actions area */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Action button */}
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className={`
                  px-3 py-1.5 text-body-sm font-medium rounded-md
                  ${styles.action}
                  focus:outline-none focus-visible:ring-state-focus
                  transition-colors duration-fast
                `}
              >
                {action.label}
              </button>
            )}

            {/* Dismiss button */}
            {dismissible && (
              <button
                type="button"
                onClick={onDismiss}
                className={`
                  p-1.5 -m-1.5 rounded-md
                  ${styles.dismiss}
                  focus:outline-none focus-visible:ring-state-focus
                  transition-colors duration-fast
                `}
                aria-label="Dismiss banner"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Banner;
